import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  ViewEncapsulation,
} from '@angular/core';
import {
  FaIconComponent,
  FaIconLibrary,
} from '@fortawesome/angular-fontawesome';
import {
  faArrowRightFromBracket,
  faFlask,
  faGear,
  faPalette,
} from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { selectUser } from '@core/states/auth/auth.selectors';
import { AppState } from '@core/states/app.state';
import * as AuthActions from '@core/states/auth/auth.actions';
import {
  DialogService,
  MenuComponent,
  MenuItemComponent,
} from '@group24ag/flowkit';
import { FeatureCenterDialogComponent } from '@core/feature-preview/dialog/feature-center-dialog.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { SidebarThemeSwitchComponent } from '@core/components/sidebar/components/user-menu/theme/sidebar-theme-switch.component';
import { Router } from '@angular/router';
import { ColorThemeDialogComponent } from '@core/theming/dialog/color-theme-dialog.component';

interface MenuItem {
  type: 'item';
  label: string;
  icon: IconProp;
  action: () => void;
}

interface MenuSeparator {
  type: 'separator';
}

type MenuEntry = MenuItem | MenuSeparator;

@Component({
  selector: 'app-sidebar-user-menu-content',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    FaIconComponent,
    MenuComponent,
    MenuItemComponent,
    SidebarThemeSwitchComponent,
  ],
  host: {
    '[style.width.px]': 'width()',
  },
  styles: [
    `
      .user-menu .flkMenu__container {
        margin-left: 3px;
        margin-right: 3px;
        margin-bottom: 7px !important;
      }
    `,
  ],
  template: `
    <flk-menu class="user-menu">
      @if (user()) {
        <div class="flex w-full justify-between px-3 pt-1.5">
          <span class="dark:text-dune-0" i18n="@@sidebar.user-menu.theme">
            Theme:
          </span>
          <app-sidebar-theme-switch />
        </div>

        @for (entry of menuEntries; track $index) {
          @if (entry.type === 'separator') {
            <div class="flex w-full items-center px-1">
              <div
                class="dark:border-dune-800 border-dune-200 my-1.5 w-full border-b-2"></div>
            </div>
          } @else {
            <div flkMenuItem (click)="entry.action()">
              <div class="flex w-full gap-1.5">
                <fa-icon class="size-6 justify-start" [icon]="entry.icon" />
                <span>{{ entry.label }}</span>
              </div>
            </div>
          }
        }
      }
    </flk-menu>
  `,
})
export class SidebarUserMenuContentComponent {
  /** INJECTIONS **/
  /****/
  private readonly store = inject(Store<AppState>);

  /****/
  private readonly dialogService = inject(DialogService);

  /****/
  private readonly router = inject(Router);

  /** INPUTS **/
  /****/
  readonly width = input<number>(0);

  /** PROPERTIES **/
  /****/
  protected readonly user = this.store.selectSignal(selectUser);

  /****/
  protected readonly menuEntries: MenuEntry[] = [
    { type: 'separator' },
    {
      type: 'item',
      label: $localize`:@@sidebar.user-menu.colors:Colors`,
      icon: ['fas', 'palette'],
      action: () => this.onColors(),
    },
    { type: 'separator' },
    {
      type: 'item',
      label: $localize`:@@sidebar.user-menu.feature-center:Feature Center`,
      icon: ['fas', 'flask'],
      action: () => this.onFeatureCenter(),
    },
    { type: 'separator' },
    {
      type: 'item',
      label: $localize`:@@sidebar.user-menu.settings:Settings`,
      icon: ['fas', 'gear'],
      action: () => this.onSettings(),
    },
    {
      type: 'item',
      label: $localize`:@@sidebar.user-menu.sign-out:Sign out`,
      icon: ['fas', 'arrow-right-from-bracket'],
      action: () => this.onLogout(),
    },
  ];

  constructor(library: FaIconLibrary) {
    library.addIcons(faGear, faFlask, faArrowRightFromBracket, faPalette);
  }

  private onColors() {
    this.dialogService.open(ColorThemeDialogComponent, {
      title: $localize`:@@sidebar.user-menu.colors.dialog.title:Colors`,
      subTitle: 'Adjust the main color and the status colors',
      width: '46rem',
      maxWidth: '90%',
      height: '70%',
      version: 'v2',
      icon: ['fas', 'palette'],
      noContentPadding: true,
    });
  }

  private onSettings() {
    this.router.navigate(['/profile']);
  }

  private onLogout() {
    this.store.dispatch(AuthActions.logout());
  }

  private onFeatureCenter() {
    this.dialogService
      .open(FeatureCenterDialogComponent, {
        title: $localize`:@@sidebar.user-menu.feature-center.dialog.title:Feature center`,
        subTitle: 'Enable or disable special or experimental features',
        width: '60rem',
        maxWidth: '80%',
        height: '65%',
        version: 'v2',
        icon: ['fas', 'flask'],
        noContentPadding: true,
      })
      .subscribe(res => {
        if (res) location.reload(); // Page refresh to update views
      });
  }
}
